import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TriangleAlert, Check } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { apiError } from '../lib';
import {
  BLANK_CURRENCY,
  ROUNDING_MODES,
  formToState,
  formatAsDefined,
  isoVerdict,
  type CurrencyForm,
  type CurrencyRoundingModeValue,
  type PlatformCurrency,
} from './lib';

interface Props {
  open: boolean;
  currency: PlatformCurrency | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Big enough to show grouping and a fractional part in every minor unit. */
const DEFAULT_SAMPLE = '1234.5678';

export default function CurrencyFormDialog({ open, currency, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState<CurrencyForm>(BLANK_CURRENCY);
  const [sample, setSample] = useState(DEFAULT_SAMPLE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSample(DEFAULT_SAMPLE);
    setForm(currency ? formToState(currency) : BLANK_CURRENCY);
  }, [open, currency]);

  const set = <K extends keyof CurrencyForm>(key: K, value: CurrencyForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const decimals = Number(form.decimals);
  const code = form.code.trim().toUpperCase();
  const iso = isoVerdict(code, decimals);

  // `formatCurrency` needs a real ISO code or Intl throws; fall back until the
  // admin has typed all three letters.
  const previewCode = code.length === 3 ? code : 'USD';
  const rendered = formatCurrency(sample, previewCode, i18n.language);
  const asDefined = formatAsDefined(
    sample,
    { symbol: form.symbol, decimals, symbolFirst: form.symbolFirst },
    i18n.language,
  );

  const submit = async () => {
    const next: Record<string, string> = {};
    if (!/^[A-Za-z]{3}$/.test(form.code.trim())) next.code = t('currency.admin.codeFormat');
    if (!form.name.trim()) next.name = t('settings.validation.required');
    if (!form.symbol.trim()) next.symbol = t('settings.validation.required');
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 4) {
      next.decimals = t('currency.admin.decimalsRange');
    }
    if (form.sortOrder && !Number.isInteger(Number(form.sortOrder))) {
      next.sortOrder = t('settings.validation.number');
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    const body = {
      name: form.name.trim(),
      symbol: form.symbol.trim(),
      decimals,
      roundingMode: form.roundingMode,
      symbolFirst: form.symbolFirst,
      isActive: form.isActive,
      sortOrder: Number(form.sortOrder) || 0,
    };

    setSaving(true);
    try {
      if (currency) {
        await api.patch(`/currencies/${currency.id}`, body);
        toast.success(t('currency.admin.updated'));
      } else {
        // The code is the natural key, so it is only ever set at creation.
        await api.post('/currencies', { ...body, code });
        toast.success(t('currency.admin.created'));
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(apiError(error, t('currency.admin.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={currency ? t('currency.admin.editTitle') : t('currency.admin.createTitle')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? t('settings.common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('currency.code')}
            required
            error={errors.code}
            hint={currency ? undefined : t('currency.codeHint')}
          >
            <Input
              value={form.code}
              dir="ltr"
              className="text-start uppercase"
              maxLength={3}
              disabled={!!currency}
              autoFocus={!currency}
              onChange={(e) => set('code', e.target.value.toUpperCase())}
            />
          </Field>
          <Field label={t('currency.name')} required error={errors.name}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label={t('currency.symbol')} required error={errors.symbol}>
            <Input value={form.symbol} onChange={(e) => set('symbol', e.target.value)} />
          </Field>
          <Field
            label={t('currency.decimals')}
            required
            error={errors.decimals}
            hint={t('currency.decimalsHint')}
          >
            <Input
              type="number"
              min="0"
              max="4"
              step="1"
              dir="ltr"
              className="text-start"
              value={form.decimals}
              onChange={(e) => set('decimals', e.target.value)}
            />
          </Field>
          <Field label={t('currency.roundingMode')}>
            <Select
              value={form.roundingMode}
              onChange={(e) => set('roundingMode', e.target.value as CurrencyRoundingModeValue)}
            >
              {ROUNDING_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`currency.rounding.${mode}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('currency.symbolPlacement')}>
            <Select
              value={form.symbolFirst ? 'first' : 'last'}
              onChange={(e) => set('symbolFirst', e.target.value === 'first')}
            >
              <option value="first">{t('currency.symbolBefore')}</option>
              <option value="last">{t('currency.symbolAfter')}</option>
            </Select>
          </Field>
          <Field label={t('currency.sortOrder')} error={errors.sortOrder} hint={t('currency.sortOrderHint')}>
            <Input
              type="number"
              step="1"
              dir="ltr"
              className="text-start"
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
            />
          </Field>
          <div className="flex items-end">
            <label className="flex items-center gap-2 pb-3 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
              />
              {t('currency.active')}
            </label>
          </div>
        </div>

        {/* The whole point of this screen: a wrong minor unit is visible here,
            not in a Kuwaiti venue's till three weeks later. */}
        <section className="rounded-lg border border-[var(--border)] bg-[var(--muted)] p-4">
          <h4 className="text-sm font-semibold">{t('currency.preview.title')}</h4>
          <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">{t('currency.preview.hint')}</p>

          <div className="mt-3 max-w-[12rem]">
            <Field label={t('currency.preview.amountLabel')}>
              <Input
                value={sample}
                dir="ltr"
                className="text-start"
                inputMode="decimal"
                onChange={(e) => setSample(e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-[var(--border)] bg-[var(--card)] p-3">
              <div className="text-xs text-[var(--muted-foreground)]">
                {t('currency.preview.rendered')}
              </div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{rendered}</div>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                {t('currency.preview.renderedHint')}
              </p>
            </div>
            <div className="rounded-md border border-[var(--border)] bg-[var(--card)] p-3">
              <div className="text-xs text-[var(--muted-foreground)]">
                {t('currency.preview.asDefined')}
              </div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{asDefined}</div>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                {t('currency.preview.definedHint')}
              </p>
            </div>
          </div>

          {iso.mismatch ? (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="text-xs">
                <div className="font-semibold">{t('currency.iso.mismatchTitle')}</div>
                <p className="mt-0.5">
                  {t('currency.iso.mismatchBody', {
                    code: code || '—',
                    expected: iso.expected,
                    actual: Number.isFinite(decimals) ? decimals : '—',
                  })}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => set('decimals', String(iso.expected))}
                >
                  {t('currency.iso.useIso', { expected: iso.expected })}
                </Button>
              </div>
            </div>
          ) : !iso.known && code.length === 3 ? (
            <p className="mt-3 rounded-md border border-[var(--border)] bg-[var(--card)] p-3 text-xs text-[var(--muted-foreground)]">
              {t('currency.iso.unknownCode', { code })}
            </p>
          ) : iso.known ? (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-green-700">
              <Check className="h-4 w-4" />
              {t('currency.iso.match', { expected: iso.expected })}
            </p>
          ) : null}
        </section>
      </div>
    </Dialog>
  );
}
